/**
 * FIELD_OPTIONS-key → DB-column translation for the custom-column-mapping path.
 * Extracted from import-job-field-options.ts to keep that file under the
 * max-lines limit (the FIELD_OPTIONS list itself is large).
 */

/**
 * Maps each FIELD_OPTIONS `key` (the UI/custom-mapping namespace, e.g. 'type',
 * 'supplier', 'grw', 'cost_rent') to the DB column key consumed by `validateRow`
 * and the worker upsert (`media_type`, 'name', 'grw_id', 'rent', …).
 *
 * Why this exists: the standard template path (`applyColumnMapping` +
 * `XLSX_TEMPLATE_TRANSLATION`) produces rows keyed by DB column names, but the
 * custom-column-mapping path stores `mappedTo` as FIELD_OPTIONS keys (returned by
 * `suggestColumnMapping`). Without translation, a custom-mapped row keyed by
 * `type`/`supplier`/`grw`/`cost_rent` is invisible to `validateRow` (which reads
 * `media_type`/`name`/`grw_id`) and to the worker — so every required field
 * fails and nothing is imported (CU-86ca6pvjh).
 *
 * Keys whose FIELD_OPTIONS key already equals the DB column (e.g. 'city',
 * 'address', 'region', 'material') are still listed so the map is exhaustive and
 * drift-checked by tests. All five `tag_1..tag_5` keys map to the sentinel
 * `tag` column; `buildCustomMappedRow` accumulates them into `tags: string[]`.
 */
export const FIELD_OPTION_KEY_TO_DB_COLUMN: Record<string, string> = {
  // Location
  region: 'region',
  district: 'county',
  agglomeration: 'agglomeration',
  city: 'city',
  address: 'address',
  road_number: 'road_number',
  latitude: 'latitude',
  longitude: 'longitude',
  // Identification
  grw: 'grw_id',
  videocard_link: 'videocard_link',
  identifier: 'supplier_number',
  supplier: 'name',
  type: 'media_type',
  format: 'code',
  // Physical
  width_m: 'width_m',
  height_m: 'height_m',
  dimension_notes: 'dimension_notes',
  material: 'material',
  lighting: 'lighting',
  // Audience
  audience_estimate: 'audience_estimate',
  // Multiformat
  multiformat: 'multiformat',
  // Common metadata — each header maps 1:1 to its own dedicated column.
  wf_contact_person: 'wf_contact_person',
  location_description: 'location_description',
  notes: 'notes',
  // Mural variant flags
  mural_3d: 'mural_element_3d',
  mural_led_tape: 'mural_led_tape',
  mural_neon_led: 'mural_neon_led',
  mural_fluo_uv: 'mural_fluo_uv',
  // Costs / client prices
  cost_rent: 'rent',
  cost_exposure: 'exposure_cost',
  price_catalog: 'catalog_price',
  price_discounted: 'discounted_price',
  price_last_minute: 'last_minute_price',
  price_starter_rw: 'starter_price',
  cost_production: 'production_cost',
  client_production: 'client_production',
  cost_installation: 'installation_cost',
  client_installation: 'client_installation',
  cost_dismantling: 'dismantling_cost',
  client_dismantling: 'client_dismantling',
  cost_additional: 'additional_costs',
  client_additional_fees: 'client_additional_fees',
  mural_eco_production: 'mural_eco_production',
  // Tags — all five fold into the sentinel `tag` column (accumulated into tags[])
  tag_1: 'tag',
  tag_2: 'tag',
  tag_3: 'tag',
  tag_4: 'tag',
  tag_5: 'tag',
  // Photos
  photo_urls: 'photo_urls',
};

/** Sentinel DB column used by tag FIELD_OPTIONS keys; rows with this target accumulate into tags[]. */
export const TAG_DB_COLUMN = 'tag';

/**
 * Resolves a custom-mapping `mappedTo` value to the DB column key consumed by
 * validateRow and the worker. FIELD_OPTIONS keys are translated; values that are
 * already DB column keys (e.g. legacy saved mappings or test fixtures using
 * 'media_type'/'name'/'code'/'grw_id') pass through unchanged.
 */
export function resolveMappedToColumn(mappedTo: string): string {
  return FIELD_OPTION_KEY_TO_DB_COLUMN[mappedTo] ?? mappedTo;
}
